import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "./authService";
import { fetchUserProfile, switchRole, verifyRoleOtp } from "../user/userSlice";
import { setAuthToken } from "../../services/apiClient";
import { setUser as saveUserToStorage } from "../../utils/token";

// Thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, thunkAPI) => {
    try {
      const data = await authService.login(credentials);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Login failed");
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, thunkAPI) => {
    try {
      const data = await authService.register(userData);
      return data; // adjust based on your API response
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Registration failed");
    }
  },
);

export const requestOtp = createAsyncThunk(
  "auth/requestOtp",
  async (emailData, thunkAPI) => {
    try {
      const data = await authService.requestOtp(emailData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Request Otp failed");
    }
  },
);

export const verifyOtpAfterRegister = createAsyncThunk(
  "auth/verifyOtpAfterRegister",
  async (otpData, thunkAPI) => {
    try {
      const data = await authService.verifyOtp(otpData);
      return { ...data, role: data.role, roles: data.roles };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Otp Verification failed",
      );
    }
  },
);

export const verifyOtpForLogin = createAsyncThunk(
  "auth/verifyOtpForLogin",
  async (otpData, thunkAPI) => {
    try {
      const res = await authService.verifyLoginOtp(otpData);
      return { ...res, role: res.role, roles: res.roles };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Otp Verification failed",
      );
    }
  },
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (otpData, thunkAPI) => {
    try {
      const data = await authService.resendOtp(otpData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Resend Otp request failed",
      );
    }
  },
);

export const resendVerification = createAsyncThunk(
  "auth/resendVerification",
  async (emailData, thunkAPI) => {
    try {
      const data = await authService.resendVerification(emailData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Resend Verification request failed",
      );
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (passwordData, thunkAPI) => {
    try {
      const data = await authService.forgotPassword(passwordData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Forgot Password failed",
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (passwordData, thunkAPI) => {
    try {
      const data = await authService.resetPassword(passwordData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Reset Password failed");
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, thunkAPI) => {
    try {
      await authService.logout(); // API + local cleanup
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Logout failed");
    }
  },
);

const initialState = {
  user: null,
  loading: false,
  error: null,
  token: localStorage.getItem("access_token") || null,
  tokenExpiry: localStorage.getItem("token_expiry") || null,
  role: localStorage.getItem("role") || null,
  roles: JSON.parse(localStorage.getItem("roles") || "[]"),
  isAuthenticated: !!localStorage.getItem("access_token"),
  registeredEmail: null, // to store email after registration
  otpVerified: false,
  kycVerified: null,
  certificationVerified: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      authService.logout(); // removes token
      state.user = null;
      state.token = null;
      state.tokenExpiry = null;
      state.isAuthenticated = false;
      state.role = null;
      state.roles = [];
      state.kycVerified = null;
      state.certificationVerified = null;
      localStorage.removeItem("access_token");
      localStorage.removeItem("role");
      localStorage.removeItem("token_expiry");
    },
    // setCredentials: (state, action) => {
    //   // console.log("setCredentials payload:", action.payload);
    //   const { user, token, role } = action.payload;
    //   // console.log("setCredentials token:", token);
    //   const parsedRoles = user?.roles ? JSON.parse(user.roles) : [];
    //   state.user = user;
    //   state.token = token;
    //   state.role = role || parsedRoles[0] || user?.role || null;
    //   state.isAuthenticated = !!token;

    //   state.kycVerified = user.kyc_verified;
    //   state.certificationVerified = user.certification_verified;

    //   // persist to localStorage immediately
    //   if (token) localStorage.setItem("token", token);
    //   if (state.role) localStorage.setItem("role", state.role);
    // },
    setCredentials: (state, action) => {
      const { user, token, access_token, role, expiresIn } =
        action.payload || {};
      const finalToken = token || access_token; // normalize both cases

      // Keep old user if not provided (role switch case)
      let parsedRoles = [];
      try {
        const rawRoles = user?.roles;
        if (Array.isArray(rawRoles)) {
          parsedRoles = rawRoles;
        } else if (typeof rawRoles === "string") {
          parsedRoles = JSON.parse(rawRoles);
        }
      } catch (e) {
        console.error("Failed to parse roles in setCredentials:", e);
      }
      if (user) {
        state.user = user;
        state.kycVerified = user.kyc_verified;
        state.certificationVerified = user.certification_verified;
      }

      // Only update token and role if present
      if (finalToken) state.token = finalToken;
      if (role)
        state.role = role || parsedRoles[0] || user?.role || state.role || null;

      // Update expiry if provided
      if (expiresIn) {
        const expiryTime = Date.now() + expiresIn * 1000;
        state.tokenExpiry = expiryTime;
        localStorage.setItem("token_expiry", expiryTime);
      }

      // Maintain authentication flag
      state.isAuthenticated = !!(finalToken || state.token);

      // Persist immediately (use consistent key)
      if (finalToken) localStorage.setItem("access_token", finalToken);
      if (state.role) localStorage.setItem("role", state.role);
      // ✅ Persist roles to localStorage and update state
      if (parsedRoles.length > 0) {
        state.roles = parsedRoles;
        localStorage.setItem("roles", JSON.stringify(parsedRoles));
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        // console.log("LOGIN SUCCESS:", action.payload);
        state.loading = false;
        state.user = action.payload?.user;
        state.token = action.payload?.access_token || action.payload?.token;
        state.isAuthenticated = true;

        if (action.payload?.expiresIn) {
          const expiryTime = Date.now() + action.payload.expiresIn * 1000;
          state.tokenExpiry = expiryTime;
          localStorage.setItem("token_expiry", expiryTime);
        }

        // Parse and store roles
        let parsedRoles = [];
        try {
          const rawRoles = action.payload?.user?.roles;
          if (Array.isArray(rawRoles)) {
            parsedRoles = rawRoles;
          } else if (typeof rawRoles === "string") {
            parsedRoles = JSON.parse(rawRoles);
          }
        } catch (e) {
          console.error("Failed to parse roles during login:", e);
        }

        state.roles = parsedRoles;
        state.role = action.payload?.role || action.payload?.user?.role || null;

        state.kycVerified = action.payload?.user?.kyc_verified;
        state.certificationVerified =
          action.payload?.user?.certification_verified;

        // persist immediately
        if (state.token) setAuthToken(state.token);
        if (state.role) localStorage.setItem("role", state.role);
        if (state.user) saveUserToStorage(state.user);
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.registeredEmail = action.payload.data.email;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Request Otp
      .addCase(requestOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Resend Verification Email
      .addCase(resendVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OTP Verification After Registration
      .addCase(verifyOtpAfterRegister.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpAfterRegister.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token || action.payload.token;
        state.role = action.payload.role;
        state.user = action.payload.user || state.user;
        state.otpVerified = true;
        state.isAuthenticated = true;

        let parsedRoles = [];
        try {
          const rawRoles = action.payload.roles;
          if (Array.isArray(rawRoles)) {
            parsedRoles = rawRoles;
          } else if (typeof rawRoles === "string") {
            parsedRoles = JSON.parse(rawRoles);
          }
        } catch (e) {
          console.error("Failed to parse roles during register verify:", e);
        }
        state.roles = parsedRoles;

        if (state.token) setAuthToken(state.token);
        if (state.role) localStorage.setItem("role", state.role);
        if (state.user) saveUserToStorage(state.user);
      })
      .addCase(verifyOtpAfterRegister.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OTP Verification for Login
      .addCase(verifyOtpForLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpForLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        if (action.payload.expiresIn) {
          const expiryTime = Date.now() + action.payload.expiresIn * 1000;
          state.tokenExpiry = expiryTime;
          localStorage.setItem("token_expiry", expiryTime);
        }

        // Parse and store roles
        let parsedRoles = [];
        try {
          const rawRoles = action.payload.roles || action.payload.user?.roles;
          if (Array.isArray(rawRoles)) {
            parsedRoles = rawRoles;
          } else if (typeof rawRoles === "string") {
            parsedRoles = JSON.parse(rawRoles);
          }
        } catch (e) {
          console.error("Failed to parse roles during login verify:", e);
        }

        state.roles = parsedRoles;
        state.role = action.payload.role;

        state.user = action.payload.user || state.user;
        // Correctly set KYC and Certification status from the fresh user object
        state.kycVerified =
          action.payload.user?.kyc_verified ?? state.kycVerified;
        state.certificationVerified =
          action.payload.user?.certification_verified ??
          state.certificationVerified;

        if (state.token) setAuthToken(state.token);
        if (state.role) localStorage.setItem("role", state.role);
        if (state.user) saveUserToStorage(state.user);
      })

      .addCase(verifyOtpForLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Resend Otp
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.tokenExpiry = null;
        state.isAuthenticated = false;
        state.role = null;
        state.roles = [];
        state.kycVerified = null;
        state.certificationVerified = null;
        state.error = null;
        localStorage.removeItem("access_token");
        localStorage.removeItem("role");
        localStorage.removeItem("token_expiry");
      })

      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.tokenExpiry = null;
        state.isAuthenticated = false;
        state.role = null;
        state.roles = [];
        state.kycVerified = null;
        state.certificationVerified = null;
        state.error = null;
      })

      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        state.kycVerified = action.payload?.kyc_verified;
        state.certificationVerified = action.payload?.certification_verified;

        // Keep localStorage in sync
        localStorage.setItem("app_user", JSON.stringify(state.user));
      })
      .addCase(switchRole.fulfilled, (state, action) => {
        const payload = action.payload;
        const resNested = payload?.data;

        const token =
          resNested?.token ||
          payload?.token ||
          resNested?.access_token ||
          payload?.access_token;
        const role =
          resNested?.current_role ||
          resNested?.role ||
          payload?.current_role ||
          payload?.role;

        if (token) {
          state.token = token;
          setAuthToken(token);
          localStorage.setItem("access_token", token);
        }
        if (role) {
          state.role = role;
          localStorage.setItem("role", role);
          if (state.user) {
            state.user.role = role;
          }
        }
        state.isAuthenticated = !!(token || state.token);
      })
      .addCase(verifyRoleOtp.fulfilled, (state, action) => {
        const payload = action.payload;
        const resNested = payload?.data;

        const token =
          resNested?.token ||
          payload?.token ||
          resNested?.access_token ||
          payload?.access_token;
        const role =
          resNested?.current_role ||
          resNested?.role ||
          payload?.current_role ||
          payload?.role;

        if (token) {
          state.token = token;
          setAuthToken(token);
          localStorage.setItem("access_token", token);
        }
        if (role) {
          state.role = role;
          localStorage.setItem("role", role);
        }
        state.isAuthenticated = !!(token || state.token);
      });
  },
});

export const { logout, setCredentials, clearError } = authSlice.actions;
export default authSlice.reducer;
